import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { ReactNode, useEffect, useRef, useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { formatString } from "../app_utils/utils/Utils";
import { MISSING_FORM_INPUT_MESSAGE } from "../app_utils/constants/ErrorMessages";
import { CSS_COL_6, CSS_COL_12 } from "../app_utils/constants/Constants";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import * as labels from "../app_utils/constants/Labels";

interface ModalType {
  children?: ReactNode;
  messageRef?: any;
  record: any;
  reloadFn: any;
  isOpen: boolean;
  toggle: () => void;
}

interface RecordType {
  id: number;
  countryId: number;
  pricePerCredit: number;
  currencyCode: string;
}

interface CountryType {
  id: number;
  name: string;
  currencyCode: string;
  currencySymbol: string;
}

const CountryMarkupFormDialog = (props: ModalType) => {
  const [record, setRecord] = useState<RecordType | null>(props?.record);
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const message = useRef<any>();

  const fetchCountriesFromServer = () => {
    new BaseApiServiceImpl("/api/v1/lookups/countries")
      .getRequestWithJsonResponse({ offset: 0, limit: 500 })
      .then(async (response) => {
        setCountries(response?.records);
      })
      .catch((error) => {
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  useEffect(() => {
    setRecord(props?.record);
    fetchCountriesFromServer();
  }, []);

  useEffect(() => {
    if (record?.countryId && countries.length > 0) {
      const selectedCountry = countries.find((c) => c.id === record.countryId);
      if (selectedCountry) {
        setSelectedCurrency(selectedCountry.currencyCode || "");
        setRecord((prevState: any) => ({
          ...prevState,
          currencyCode: selectedCountry.currencyCode || "",
        }));
      }
    }
  }, [record?.countryId, countries]);

  const clearForm = () => {
    setRecord(null);
    setSelectedCurrency("");
  };

  const onCountryChange = (e: any) => {
    const countryId = e.value;
    const selectedCountry = countries.find((c) => c.id === countryId);
    setSelectedCurrency(selectedCountry?.currencyCode || "");
    setRecord((prevState: any) => ({
      ...(prevState || {}),
      countryId: countryId,
      currencyCode: selectedCountry?.currencyCode || "",
    }));
  };

  const closeDialog = () => {
    props.toggle();
  };

  const validateForm = () => {
    if (!record?.countryId) {
      MessageUtils.showErrorMessage(message, formatString(MISSING_FORM_INPUT_MESSAGE, "Country"));
      return false;
    }
    if (!record?.pricePerCredit || record.pricePerCredit <= 0) {
      MessageUtils.showErrorMessage(message, formatString(MISSING_FORM_INPUT_MESSAGE, "Price per Credit"));
      return false;
    }
    return true;
  };

  const saveRecord = () => {
    if (validateForm()) {
      setIsSaving(true);
      new BaseApiServiceImpl("/api/v1/country-markups")
        .postRequestWithJsonResponse(record)
        .then(async (response) => {
          setIsSaving(false);
          clearForm();
          MessageUtils.showSuccessMessage(
            props?.messageRef,
            labels.LABEL_RECORD_SAVED_SUCCESSFULLY
          );
          closeDialog();
          props?.reloadFn();
        })
        .catch((error) => {
          setIsSaving(false);
          MessageUtils.showErrorMessage(message, error.message);
        });
    }
  };

  const dialogFooter = (
    <>
      <Button
        label={labels.LABEL_CANCEL}
        icon={PrimeIcons.TIMES}
        className="p-button-text"
        onClick={closeDialog}
      />
      <Button
        label={labels.LABEL_SAVE}
        icon={PrimeIcons.SAVE}
        className="p-button-secondary"
        onClick={saveRecord}
        loading={isSaving}
      />
    </>
  );

  return (
    <Dialog
      visible={props.isOpen}
      header={"Add/Edit Country Price Per Credit"}
      footer={dialogFooter}
      modal
      className="p-fluid"
      onHide={closeDialog}
      style={{ width: "50vw" }}
    >
      <div className="grid">
        <div className="col-12">
          <Messages ref={message} style={{ width: "100%" }} />
        </div>
        <div className={CSS_COL_6}>
          <label htmlFor="country">Country</label>
          <Dropdown
            id="country"
            value={record?.countryId}
            options={countries}
            optionValue="id"
            optionLabel="name"
            onChange={onCountryChange}
            placeholder="Select a country"
            style={{ width: "100%" }}
          />
        </div>
        <div className={CSS_COL_6}>
          <label htmlFor="pricePerCredit">Price per Credit</label>
          <div className="p-inputgroup">
            <InputNumber
              id="pricePerCredit"
              value={record?.pricePerCredit}
              onValueChange={(e: any) =>
                setRecord((prevState: any) => ({
                  ...prevState,
                  pricePerCredit: e.value,
                }))
              }
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={4}
              placeholder="Enter price"
            />
            {selectedCurrency && (
              <span className="p-inputgroup-addon">{selectedCurrency}</span>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default CountryMarkupFormDialog;
